'use client';

import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import type { NavItems } from '@/types/nav';
import { useMenuTypeList, useSportMenuItemGetter } from '@/hooks/query/use-menu-data';
import { useMenuTypeActived } from '@/hooks/use-menu-type-actived';

import { SimpleAccordion, SimpleAccordionItem } from '@nf/ui';
import { MenuWrapper } from '../menu-wrapper';
import { SidebarItem } from '../sidebar-item';
import { getLeagueIconName } from '@/components/icons/svg-icons';
import { useSideBarContext } from '../../contexts/sidebar-context';
import { EventMode, menuDefinition } from '@/types/menu-setting';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { sportsbookService } from '@/services/sportsbook.service';
import { MULTI_SPORT_MENU_MODE } from '@/constants/sport-menu-mode';
import { sidebarStyle } from '../../sidebar.style';
import { ViewEnum } from '@/types';
import clsx from 'clsx';
import { useAppearanceStore } from '@/store/appearance';

export const SidebarWorldMenu = () => {
	const { filterSabaString } = useFilterSabaString();
	const params = useParams();
	const t = useTranslations('General');
	const { getSportMenuItemsOfMode } = useSportMenuItemGetter();
	const { isMenuTypeActived, isMenuTypeSportActived } = useMenuTypeActived();
	const selectedCategory = useSideBarContext().selectedCategory;
	const { menuTypeList } = useMenuTypeList();
	const { SiteConfigsData } = useGetSiteConfigs();
	const order = Object.keys(menuDefinition);
	const sortedMenuTypeList = menuTypeList
		.filter(item => item.modeId > 0)
		.sort((a, b) => {
			return order.indexOf(`M${a.modeId}`) - order.indexOf(`M${b.modeId}`)
		});
	const view = useAppearanceStore(state => state.view);
	
	const menuTypeItems: NavItems = sortedMenuTypeList.map(item => {
		const eventMode = `M${item.modeId}` as EventMode;
		const isMultiSportMenuModeId = MULTI_SPORT_MENU_MODE.includes(eventMode);

		const subItems: NavItems = getSportMenuItemsOfMode(`M${item.modeId}` as EventMode)
			.map(subItem => ({
				sportId: subItem.gameId,
				href: subItem.href, 
				className: 'side-sub-menu__btn',
				icon: subItem.icon, 
				iconType: 'sport',
				type: 'collapsible',
				title: subItem.name, 
				amount: subItem.matchCount,
				selected: isMenuTypeSportActived(`${subItem.href}`)
			}));

		return {
			href: isMultiSportMenuModeId ? `/world/${item.modeType}` : subItems[0].href,
			className: 'side-menu__btn',
			icon: getLeagueIconName(item.modeType),
			iconType: 'sport',
			type: 'collapsible',
			title: filterSabaString(t(sportsbookService.makeMenuNodeResourceKey(SiteConfigsData?.EnableFilterSaba, item.modeId.toString()))),
			amount: item.matchCount,
			selected: isMenuTypeActived(`/world/${item.modeType}`),
			subItems: isMultiSportMenuModeId ? subItems : undefined,
		};
	});


	return selectedCategory === 'Sports' && menuTypeItems.length > 0 ? (
		<MenuWrapper title={t('lbl_HotEvents')}>
			<SimpleAccordion defaultValue={`/world/${params.mode}`}>
				{menuTypeItems.map(item => (
					<SimpleAccordionItem 
						key={item.href}
						className={clsx('side-menu__item', { [sidebarStyle.sideMenuItem]: view === ViewEnum.mars && item.selected })}
						value={item.href}
						hasSummary={false}
					>
						<SidebarItem item={item}>
							{item.title}
						</SidebarItem>
					</SimpleAccordionItem>
				))}
			</SimpleAccordion>
		</MenuWrapper>
	) : null;
};